import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import { LogOut } from 'lucide-react';
import { toast } from 'sonner';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Button } from '../components/ui/button';
import { EstimateCard } from '../components/owner/EstimateCard';
import { ChangePasswordDialog } from '../components/owner/ChangePasswordDialog';
import { StatsBar } from '../components/owner/StatsBar';
import '../owner.css';

const API = process.env.REACT_APP_BACKEND_URL;
const TOKEN_KEY = 'pbnj_owner_token';
const TABS = [
  { id: 'open', label: 'Open', match: (s) => ['new', 'called', 'quoted'].includes(s) },
  { id: 'won', label: 'Won', match: (s) => s === 'won' },
  { id: 'lost', label: 'Lost', match: (s) => s === 'lost' },
  { id: 'all', label: 'All', match: () => true },
];

const errText = (e) => {
  const d = e.response?.data?.detail;
  if (typeof d === 'string') return d;
  if (Array.isArray(d)) return d.map((x) => x.msg).join(' ');
  return 'Something went wrong. Please try again.';
};

const LoginForm = ({ onLogin }) => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true); setError('');
    try {
      const { data } = await axios.post(`${API}/api/auth/login`, { email, password });
      onLogin(data.token);
    } catch (err) { setError(errText(err)); } finally { setBusy(false); }
  };
  return (
    <form onSubmit={submit} className="o-login" data-testid="owner-login-form">
      <h1>Owner login</h1>
      <div><Label htmlFor="o-email">Email</Label><Input id="o-email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} required data-testid="owner-email-input" /></div>
      <div><Label htmlFor="o-pass">Password</Label><Input id="o-pass" type="password" value={password} onChange={(e) => setPassword(e.target.value)} required data-testid="owner-password-input" /></div>
      {error && <p className="o-error" data-testid="owner-login-error">{error}</p>}
      <Button type="submit" className="w-btn-primary" disabled={busy} data-testid="owner-login-submit">{busy ? 'Signing in...' : 'Sign in'}</Button>
      <Link to="/" className="o-back">Back to website</Link>
    </form>
  );
};

const Tabs = ({ items, tab, setTab }) => (
  <div className="o-tabs" role="tablist" data-testid="owner-tabs">
    {TABS.map((t) => (
      <button key={t.id} role="tab" aria-selected={tab === t.id} className={tab === t.id ? 'is-on' : ''}
        onClick={() => setTab(t.id)} data-testid={`owner-tab-${t.id}`}>
        {t.label} <span data-testid={`owner-tab-count-${t.id}`}>{items ? items.filter((e) => t.match(e.status)).length : 0}</span>
      </button>
    ))}
  </div>
);

export default function OwnerPage() {
  const [token, setToken] = useState(() => localStorage.getItem(TOKEN_KEY));
  const [items, setItems] = useState(null);
  const [tab, setTab] = useState('open');
  const shown = useMemo(() => items?.filter((e) => TABS.find((t) => t.id === tab).match(e.status)), [items, tab]);

  const logout = () => { localStorage.removeItem(TOKEN_KEY); setToken(null); setItems(null); };
  const login = (t) => { localStorage.setItem(TOKEN_KEY, t); setToken(t); };
  const [statsKey, setStatsKey] = useState(0);
  const updateItem = (next) => {
    setItems((list) => list.map((e) => (e.id === next.id ? next : e)));
    setStatsKey((k) => k + 1);
  };

  useEffect(() => {
    if (!token) return;
    axios.get(`${API}/api/owner/estimates`, { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => setItems(r.data))
      .catch((err) => {
        if (err.response?.status === 401) logout();
        else toast.error("Couldn't load estimate requests", { description: 'Please refresh the page.' });
      });
  }, [token]);

  if (!token) return <div className="site-theme o-page"><LoginForm onLogin={login} /></div>;

  return (
    <div className="site-theme o-page" data-testid="owner-page">
      <header className="o-header">
        <div><h1>Estimate requests</h1><p>{items ? `${items.length} total` : 'Loading...'}</p></div>
        <div className="o-actions">
          <ChangePasswordDialog token={token} onToken={login} />
          <Button variant="outline" onClick={logout} data-testid="owner-logout"><LogOut size={16} /> Log out</Button>
        </div>
      </header>
      <StatsBar token={token} refreshKey={statsKey} />
      <Tabs items={items} tab={tab} setTab={setTab} />
      {shown && shown.length === 0 && <p className="o-empty" data-testid="owner-empty">No estimate requests here.</p>}
      <div className="o-list">{shown?.map((e) => <EstimateCard key={e.id} e={e} token={token} onChange={updateItem} />)}</div>
    </div>
  );
}
